import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { TIMEZONES } from "@/lib/vibe";
import { toast } from "sonner";
import { Plus, X, Copy, RefreshCw, LogOut, QrCode, AlertTriangle, Smartphone, Check, Tv, Bell, BellOff, Share2, Printer, Mail, Send } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { isStandalone, isIos, markInstalled } from "@/lib/installPwa";
import { isKioskDevice, setKioskDevice } from "@/lib/kioskMode";
import { isDeviceCalendarOnly, setDeviceCalendarOnly, getDeviceDashboardMode, setDeviceDashboardMode } from "@/lib/deviceCalendarOnly";
import { setTimeFormat as applyTimeFormatGlobal } from "@/lib/time";
import { pushSupported, pushPermission, enablePush, disablePush, pushSubscribedHere, resyncPush, getNotificationMode, setNotificationMode, getPushAudience, setPushAudience } from "@/lib/push";
import { EVENT_KINDS } from "@/lib/events";
import { getToddlerMemberId } from "@/lib/toddlerMode";
import { hasToddler } from "@/lib/birthdays";
import { getSettingsPinRequired, setSettingsPinRequired } from "@/lib/settingsPinLock";
import { setupPrivateNoteKey, clearPrivateNoteKey } from "@/lib/privateCrypto";
import { isWeakPin, WEAK_PIN_MESSAGE } from "@/lib/pinStrength";
import { auth } from "@/lib/api";
import HousesPanel from "@/components/HousesPanel";
import CalendarSubscriptions from "@/components/CalendarSubscriptions";
import ToddlerModeSheet from "@/components/ToddlerModeSheet";
import { resetGuide } from "@/lib/guides";
import { useI18n } from "@/lib/i18n";

function NotificationsRow({ viewerId }) {
  const [state, setState] = useState({ supported: true, enabled: false, perm: "default", busy: false });
  // Single tri-state for the whole panel:
  //   "all"     → every family event pings this device
  //   "tagged"  → only events with this member tagged ping
  //   "digest"  → one calm 7am summary; no per-event pings
  // Server-side that maps to (notification_mode, push_audience):
  //   all     → ("events", "all_family")
  //   tagged  → ("events", "tagged_only")
  //   digest  → ("digest", "all_family")  // audience is moot in digest mode
  const [pingMode, setPingMode] = useState("all");
  const [pingBusy, setPingBusy] = useState(false);
  const [skipImports, setSkipImports] = useState(false);
  const [diag, setDiag] = useState(null); // {loading, error, result}
  // Whether to show inactive (dead) subs in the diagnose panel.
  // Collapsed by default — they're noise that drowns the few active
  // ones (a real prod family had 25 inactive vs 2 active).
  const [showInactiveSubs, setShowInactiveSubs] = useState(false);
  const [cleaningInactive, setCleaningInactive] = useState(false);

  useEffect(() => {
    const sup = pushSupported();
    if (!sup) {
      setState((s) => ({ ...s, supported: false }));
      return;
    }
    pushSubscribedHere().then(async (enabled) => {
      setState({ supported: true, enabled, perm: pushPermission(), busy: false });
      if (enabled) {
        try {
          const m = await getNotificationMode();
          if (m === "digest") {
            setPingMode("digest");
          } else {
            try {
              const aud = await getPushAudience();
              setSkipImports(!!aud.skip_imports);
              setPingMode(aud.push_audience === "tagged_only" ? "tagged" : "all");
            } catch {
              setPingMode("all");
            }
          }
        } catch { /* default to "all" */ }
        try {
          const aud = await getPushAudience();
          setSkipImports(!!aud.skip_imports);
        } catch { /* default false */ }
      }
    });
  }, []);

  const turnOn = async () => {
    setState((s) => ({ ...s, busy: true }));
    try {
      await enablePush(viewerId);
      setState({ supported: true, enabled: true, perm: "granted", busy: false });
      // Read pacing/audience after enable so the radio reflects server truth.
      try {
        const m = await getNotificationMode();
        if (m === "digest") {
          setPingMode("digest");
        } else {
          try {
            const aud = await getPushAudience();
            setSkipImports(!!aud.skip_imports);
            setPingMode(aud.push_audience === "tagged_only" ? "tagged" : "all");
          } catch {
            setPingMode("all");
          }
        }
      } catch { /* default to "all" */ }
      toast.success("Notifications on — birthdays and dog walks always come through.");
    } catch (e) {
      setState((s) => ({ ...s, busy: false }));
      toast.error(e?.message || "Couldn't turn notifications on.");
    }
  };
  const turnOff = async () => {
    setState((s) => ({ ...s, busy: true }));
    await disablePush();
    setState({ supported: true, enabled: false, perm: pushPermission(), busy: false });
    toast.success("Notifications off on this device.");
  };

  // Single setter for the 3-state radio. Persists both
  // `notification_mode` and `push_audience` server-side so legacy
  // back-end filters keep working.
  const switchPing = async (next) => {
    if (next === pingMode || pingBusy) return;
    setPingBusy(true);
    const prev = pingMode;
    setPingMode(next); // optimistic
    const targetMode = next === "digest" ? "digest" : "events";
    const targetAudience = next === "tagged" ? "tagged_only" : "all_family";
    const okMode = await setNotificationMode(targetMode, viewerId);
    const okAud = await setPushAudience({ audience: targetAudience }, viewerId);
    setPingBusy(false);
    if (!okMode || !okAud) {
      setPingMode(prev);
      toast.error("Couldn't update notifications. Try again.");
      return;
    }
    toast.success(
      next === "digest"
        ? "One morning summary at 7am — quieter from now."
        : next === "tagged"
        ? "Only events with you tagged will ping."
        : "Every family event will ping this device.",
    );
  };

  const toggleSkipImports = async () => {
    if (pingBusy) return;
    setPingBusy(true);
    const next = !skipImports;
    setSkipImports(next); // optimistic
    const ok = await setPushAudience({ skip_imports: next }, viewerId);
    setPingBusy(false);
    if (!ok) {
      setSkipImports(!next);
      toast.error("Couldn't update import setting. Try again.");
    }
  };

  if (!state.supported) {
    return (
      <div className="pt-4 border-t border-[color:var(--line-soft)]">
        <label className="label-tiny block mb-2">Notifications</label>
        <div className="text-[13px] text-[color:var(--ink-2)]">
          This browser doesn't support notifications. iPhone users: make sure FamFeel is added to the home screen first.
        </div>
      </div>
    );
  }
  return (
    <div className="pt-4 border-t border-[color:var(--line-soft)]">
      <div className="flex items-center justify-between gap-2 mb-2">
        <label className="label-tiny">Notifications</label>
        {state.enabled && (
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium tracking-wide uppercase"
            style={{
              background: "#CDE6D2",
              color: "#2F5A3A",
              border: "1px solid #6F9577",
            }}
            data-testid="push-active-badge"
          >
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ background: "#2F5A3A" }}
              aria-hidden="true"
            />
            Push is active
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={state.enabled ? turnOff : turnOn}
        disabled={state.busy}
        className={`ff-btn inline-flex items-center gap-2 ${state.enabled ? "" : "ff-btn-primary"}`}
        data-testid="settings-notifications-toggle"
      >
        {state.enabled ? <BellOff className="w-4 h-4" strokeWidth={1.75} /> : <Bell className="w-4 h-4" strokeWidth={1.75} />}
        {state.busy ? "…" : state.enabled ? "Turn notifications off" : "Turn notifications on"}
      </button>
      {state.enabled && (
        <button
          type="button"
          onClick={async () => {
            try {
              let res = await api.pushTest();
              // If the browser thinks it's subscribed but the server has no
              // record at all, re-register the subscription and retry once.
              if (res.subs_found === 0) {
                const ok = await resyncPush(viewerId);
                if (ok) res = await api.pushTest();
              }
              if (res.devices_notified > 0) {
                toast.success(`Test push sent to ${res.devices_notified} device${res.devices_notified > 1 ? "s" : ""}.`);
              } else if (res.subs_found === 0) {
                toast.info("No subscription reached the server — try turning notifications off, then back on.");
              } else {
                // Subs exist but delivery failed — show the actual reason.
                const reasonLabel = {
                  vapid_rejected: "Push server rejected our credentials (VAPID mismatch). Contact support.",
                  server_vapid_missing: "Push server isn't configured (VAPID keys missing).",
                  endpoint_gone_404: "The browser's push endpoint expired. Turn notifications off then on.",
                  endpoint_gone_410: "The browser's push endpoint expired. Turn notifications off then on.",
                }[res.reason] || (
                  res.reason && res.reason.startsWith("bad_config")
                    ? `Server push config issue: ${res.reason.replace("bad_config:", "")}`
                    : res.reason && res.reason.startsWith("network_error")
                    ? `Couldn't reach push service (${res.reason.replace("network_error:", "")}). Try again in a moment.`
                    : `Delivery failed (${res.reason || "unknown"}).`
                );
                toast.error(reasonLabel);
              }
            } catch (e) {
              const msg = e?.response?.data?.detail || "Couldn't send test push.";
              toast.error(msg);
            }
          }}
          className="ff-btn ml-2"
          data-testid="settings-push-test"
        >
          Send test push
        </button>
      )}
      {state.enabled && (
        <button
          type="button"
          onClick={async () => {
            if (!viewerId) {
              toast.info("Tap your tile on the dashboard first so we know which member to test for.");
              return;
            }
            setDiag({ loading: true });
            try {
              const result = await api.pushPreviewEvent(viewerId);
              setDiag({ loading: false, result });
            } catch (e) {
              const msg = e?.response?.data?.detail || "Couldn't run diagnose.";
              setDiag({ loading: false, error: String(msg) });
            }
          }}
          className="ff-btn ml-2"
          data-testid="settings-push-diagnose"
          title="Show which devices in this family would receive an event push for you, and which filter blocks them."
        >
          {diag?.loading ? "Diagnosing…" : "Diagnose"}
        </button>
      )}
      {diag?.result && (
        <div
          className="mt-3 p-3 rounded-[10px] border border-[color:var(--line-soft)] bg-[color:var(--bg-soft)] text-[13px]"
          data-testid="push-diagnose-result"
        >
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="font-medium">
              Event-push routing for member {diag.result.target_member_id?.slice(0, 8)}…
            </div>
            <button
              type="button"
              onClick={() => setDiag(null)}
              className="ff-chip text-[11px]"
              data-testid="push-diagnose-close"
            >
              Close
            </button>
          </div>
          <div className="label-tiny mb-2 text-[color:var(--ink-2)]">
            {diag.result.subs_that_would_receive}/{diag.result.subs_total} device(s) would receive an event push.
          </div>
          {diag.result.subs.length === 0 ? (
            <div className="text-[color:var(--ink-2)] italic">
              No push subscriptions registered for this family yet. Turn notifications on first.
            </div>
          ) : (() => {
            // Partition: dead subs (uninstalled / cleared cache) get
            // hidden under a "Show inactive" toggle so the panel
            // shows the live ones first. Without this, a family with
            // a few months of history can have 25 dead rows drowning
            // 2 active ones.
            const activeSubs = diag.result.subs.filter((s) => s.active);
            const inactiveSubs = diag.result.subs.filter((s) => !s.active);
            const visibleSubs = showInactiveSubs
              ? diag.result.subs
              : activeSubs;
            const handleCleanup = async () => {
              if (cleaningInactive) return;
              if (!window.confirm(
                `Delete ${inactiveSubs.length} inactive device${inactiveSubs.length === 1 ? "" : "s"} from this family? Active devices stay untouched.`,
              )) return;
              setCleaningInactive(true);
              try {
                const res = await api.pushCleanupInactive();
                toast.success(`Removed ${res.deleted} dead device${res.deleted === 1 ? "" : "s"}`);
                // Re-run diagnose so the panel refreshes.
                if (viewerId) {
                  const fresh = await api.pushPreviewEvent(viewerId);
                  setDiag({ loading: false, result: fresh });
                }
              } catch (e) {
                // Surface the real reason — usually one of:
                //   • 404 (endpoint not deployed yet on this server)
                //   • 401 (session expired)
                //   • network error
                // Generic "Cleanup failed" was unhelpful in production.
                const status = e?.response?.status;
                const detail = e?.response?.data?.detail;
                let msg = "Cleanup failed.";
                if (status === 404) {
                  msg = "Cleanup unavailable — needs the latest server update. Try again after the next deploy.";
                } else if (detail) {
                  msg = `Cleanup failed: ${detail}`;
                } else if (status) {
                  msg = `Cleanup failed (HTTP ${status}).`;
                }
                toast.error(msg);
              } finally {
                setCleaningInactive(false);
              }
            };
            return (
              <>
                {inactiveSubs.length > 0 && (
                  <div
                    className="mb-3 p-2 rounded border border-[color:var(--line-soft)] bg-[color:var(--bg)] flex items-baseline justify-between gap-2 flex-wrap"
                    data-testid="push-diag-inactive-summary"
                  >
                    <div className="label-tiny text-[color:var(--ink-2)] flex-1 min-w-0">
                      <span className="text-[color:var(--ink)] font-medium">
                        {inactiveSubs.length} inactive device{inactiveSubs.length === 1 ? "" : "s"}
                      </span>{" "}
                      hidden — likely uninstalled or cache cleared.
                      Auto-pruned after 30 days.
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setShowInactiveSubs((v) => !v)}
                        className="ff-chip text-[11px]"
                        data-testid="push-diag-toggle-inactive"
                      >
                        {showInactiveSubs ? "Hide" : `Show all ${diag.result.subs.length}`}
                      </button>
                      <button
                        type="button"
                        onClick={handleCleanup}
                        disabled={cleaningInactive}
                        className="ff-chip text-[11px]"
                        data-testid="push-diag-cleanup"
                      >
                        {cleaningInactive ? "Cleaning…" : "Clean up now"}
                      </button>
                    </div>
                  </div>
                )}
                <ul className="space-y-2">
                  {visibleSubs.map((s) => (
                    <li
                      key={s.sub_id}
                      className={`p-2 rounded border ${
                        s.would_receive_overall
                          ? "border-[color:var(--ok,#1a7f37)]/30 bg-[color:var(--ok,#1a7f37)]/5"
                          : "border-[color:var(--line-soft)]"
                      }`}
                      data-testid={`push-diag-sub-${s.sub_id}`}
                    >
                      <div className="flex items-baseline justify-between gap-2 flex-wrap">
                        <div className="font-mono text-[11px] truncate flex-1">
                          {s.user_agent || "(no user-agent)"}
                        </div>
                        <span
                          className={`label-tiny ${
                            s.would_receive_overall ? "text-[color:var(--ok,#1a7f37)]" : "text-[color:var(--danger,#a14231)]"
                          }`}
                        >
                          {s.would_receive_overall ? "RECEIVES ✓" : "BLOCKED ✗"}
                        </span>
                      </div>
                      <div className="label-tiny text-[color:var(--ink-3)] mt-1 leading-snug">
                        claimed: <span className="text-[color:var(--ink-2)]">{s.claimed_member_id ? s.claimed_member_id.slice(0, 8) + "…" : "none (shared device)"}</span>
                        {" · "}mode: <span className="text-[color:var(--ink-2)]">{s.notification_mode}</span>
                        {" · "}audience: <span className="text-[color:var(--ink-2)]">{s.push_audience}</span>
                        {!s.active && <> · <span className="text-[color:var(--danger,#a14231)]">inactive</span></>}
                      </div>
                      <div className="label-tiny text-[color:var(--ink-3)] mt-1 leading-snug">
                        targeted: <span className="text-[color:var(--ink-2)]">{s.targeted_reason}</span>
                      </div>
                      <div className="label-tiny text-[color:var(--ink-3)] leading-snug">
                        broadcast: <span className="text-[color:var(--ink-2)]">{s.broadcast_reason}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            );
          })()}
        </div>
      )}
      {diag?.error && (
        <div
          className="mt-3 p-3 rounded-[10px] border border-[color:var(--danger,#a14231)]/30 bg-[color:var(--danger,#a14231)]/5 text-[13px]"
          data-testid="push-diagnose-error"
        >
          {diag.error}
          <button
            type="button"
            onClick={() => setDiag(null)}
            className="ff-chip text-[11px] ml-2"
          >
            Close
          </button>
        </div>
      )}
      <div className="mt-3 max-w-lg text-[13px] leading-snug text-[color:var(--ink-2)]">
        Birthdays and the 30-min dog-walk heads-up always come through, no matter what's selected below.
      </div>
      {state.enabled && (
        <div
          className="mt-4 rounded-[10px] border border-[color:var(--line-soft)] bg-[color:var(--bg-soft)] p-3"
          data-testid="notification-audience-row"
        >
          <div className="label-tiny mb-2">Which events should ping this device</div>
          <div className="flex flex-col gap-1.5">
            <AudienceOption
              id="all_family"
              active={pingMode === "all"}
              onClick={() => switchPing("all")}
              busy={pingBusy}
              title="Everything in the family"
              subtitle="Every event, every member."
            />
            <AudienceOption
              id="tagged_only"
              active={pingMode === "tagged"}
              onClick={() => switchPing("tagged")}
              busy={pingBusy}
              title="Only when I'm tagged"
              subtitle="When my profile is on the event."
            />
            <AudienceOption
              id="digest"
              active={pingMode === "digest"}
              onClick={() => switchPing("digest")}
              busy={pingBusy}
              title="One morning summary at 7am"
              subtitle="Calm — one ping a day, listing today's events for you."
            />
          </div>
          <label
            className="flex items-center gap-2 mt-3 pt-3 border-t border-[color:var(--line-soft)] cursor-pointer text-[13px] text-[color:var(--ink-2)]"
            data-testid="notification-skip-imports-toggle"
          >
            <input
              type="checkbox"
              checked={skipImports}
              onChange={toggleSkipImports}
              disabled={pingBusy}
              className="cursor-pointer"
            />
            <span>
              Skip pings for events imported from external calendars
              <span className="block text-[11px] text-[color:var(--ink-3)] mt-0.5">
                Useful when you've connected a school or work calendar — you'll see them in FamFeel but won't get pings.
              </span>
            </span>
          </label>
        </div>
      )}
      {state.perm === "denied" && (
        <div className="mt-2 text-[12px] text-[color:var(--ink-3)]">
          Notifications were blocked in your browser settings — enable them there first, then come back.
        </div>
      )}
    </div>
  );
}

function AudienceOption({ id, active, onClick, busy, title, subtitle }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={`text-left p-2 rounded-[8px] border transition-colors ${
        active
          ? "border-[color:var(--ink)] bg-[color:var(--surface)]"
          : "border-[color:var(--line-soft)] hover:border-[color:var(--line)]"
      }`}
      data-testid={`notification-audience-${id}`}
      aria-pressed={active}
    >
      <div className="text-[13px] font-medium leading-tight">{title}</div>
      <div className="text-[11px] text-[color:var(--ink-3)] mt-0.5 leading-snug">{subtitle}</div>
    </button>
  );
}

function CalendarSubscribeRow() {
  // iCal subscription card — gives every family member a private URL
  // they can paste into Apple Calendar / Google Calendar / Outlook to
  // see FamFeel events alongside their own. Token-as-secret: the URL
  // is the auth credential, so a "regenerate" affordance is offered
  // for when the link has been shared by mistake. Lazy-fetched on
  // first expand (no extra request for families who never use this).
  //
  // Kind chips (categories) build a `?kinds=` filter query string
  // appended to the base URL — same token, different stream. Lets
  // one parent subscribe to just school+birthdays while another
  // wants the whole calendar.
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedKinds, setSelectedKinds] = useState([]); // [] = all

  const ensureLoaded = async () => {
    if (info || loading) return;
    setLoading(true);
    try {
      const res = await api.calendarSubscribeInfo();
      setInfo(res);
    } catch {
      toast.error("Couldn't load the subscription link.");
    } finally {
      setLoading(false);
    }
  };

  const expand = () => {
    setOpen(true);
    ensureLoaded();
  };

  // Re-derive both URLs whenever the user toggles a kind chip. We
  // append the query to the same base; the token stays the same.
  const buildUrls = () => {
    if (!info?.https_url) return { https: "", webcal: "" };
    if (selectedKinds.length === 0) {
      return { https: info.https_url, webcal: info.webcal_url };
    }
    const q = "?kinds=" + selectedKinds.join(",");
    return {
      https: info.https_url + q,
      webcal: info.webcal_url + q,
    };
  };
  const { https: shownHttps, webcal: shownWebcal } = buildUrls();

  const toggleKind = (k) => {
    setSelectedKinds((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]
    );
  };

  const copy = async () => {
    if (!shownHttps) return;
    try {
      await navigator.clipboard.writeText(shownHttps);
      toast.success("Copied — paste it into your calendar app's 'Add subscription'.");
    } catch {
      toast.error("Couldn't copy. Long-press the link and copy it manually.");
    }
  };

  const regenerate = async () => {
    if (
      !window.confirm(
        "Generate a new link? The current one will stop working — anyone subscribed to it will need the new URL."
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const res = await api.calendarSubscribeRegenerate();
      setInfo(res);
      toast.success("New link minted. The old one is now dead.");
    } catch {
      toast.error("Couldn't regenerate. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-4 border-t border-[color:var(--line-soft)]">
      <label className="label-tiny block mb-2">
        Subscribe in your phone's calendar
      </label>
      <div className="max-w-lg text-[13px] leading-snug text-[color:var(--ink-2)] mb-3">
        See FamFeel events in Apple Calendar, Google Calendar or Outlook
        alongside your own. Read-only — your phone calendar can't write
        back. Each family member can subscribe once on each device.
      </div>
      {!open ? (
        <button
          type="button"
          onClick={expand}
          className="ff-btn"
          data-testid="calendar-subscribe-open"
        >
          Get my subscription link →
        </button>
      ) : loading ? (
        <div className="text-[13px] text-[color:var(--ink-3)] italic">Loading link…</div>
      ) : info ? (
        <div className="rounded-[10px] border border-[color:var(--line-soft)] bg-[color:var(--bg-soft)] p-3 max-w-lg space-y-3">
          {/* Kind/category chips. Empty selection means "everything". */}
          <div>
            <div className="label-tiny mb-1.5">Categories (leave empty for everything)</div>
            <div className="flex flex-wrap gap-1.5">
              {EVENT_KINDS.map((kind) => {
                const active = selectedKinds.includes(kind.key);
                return (
                  <button
                    key={kind.key}
                    type="button"
                    onClick={() => toggleKind(kind.key)}
                    className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                      active
                        ? "bg-[color:var(--ink)] text-[color:var(--surface)] border-[color:var(--ink)]"
                        : "border-[color:var(--line)] hover:border-[color:var(--ink-3)] text-[color:var(--ink-2)]"
                    }`}
                    data-testid={`calendar-subscribe-kind-${kind.key}`}
                    aria-pressed={active}
                  >
                    {kind.emoji} {kind.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div
            className="text-[12px] font-mono break-all text-[color:var(--ink-2)] bg-[color:var(--surface)] p-2 rounded"
            data-testid="calendar-subscribe-url"
          >
            {shownHttps}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={copy}
              className="ff-btn"
              data-testid="calendar-subscribe-copy"
            >
              Copy link
            </button>
            <a
              href={shownWebcal}
              className="ff-btn ff-btn-primary"
              data-testid="calendar-subscribe-webcal"
              title="Opens iOS / macOS Calendar"
            >
              Add to Apple Calendar
            </a>
          </div>
          <div className="text-[12px] text-[color:var(--ink-3)] leading-snug">
            <strong>iPhone / iPad</strong>: tap "Add to Apple Calendar" above.<br />
            <strong>Google Calendar</strong>: open Google Calendar on a computer →
            "Other calendars" + → "From URL" → paste the link.<br />
            <strong>Outlook</strong>: Add calendar → Subscribe from web → paste the link.
          </div>
          <div className="pt-2 border-t border-[color:var(--line-soft)]">
            <button
              type="button"
              onClick={regenerate}
              disabled={busy}
              className="text-[12px] uppercase tracking-[0.06em] text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-40"
              data-testid="calendar-subscribe-regenerate"
            >
              {busy ? "Regenerating…" : "Regenerate link (revoke the old one)"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ToddlerModeRow({ onOpen }) {
  const [active, setActive] = useState(() => getToddlerMemberId());
  useEffect(() => {
    setActive(getToddlerMemberId());
  }, []);
  return (
    <div className="pt-4 border-t border-[color:var(--line-soft)]">
      <label className="label-tiny block mb-2">Toddler mode</label>
      <div className="max-w-lg text-[13px] leading-snug text-[color:var(--ink-2)] mb-3">
        Lock this device to one kid's check-in only — big tappable surprises
        (butterflies, bubbles, balls, fish), other cards untappable, header
        hidden.
      </div>
      <button
        type="button"
        onClick={onOpen}
        className="ff-btn"
        data-testid="toddler-mode-open"
      >
        {active ? "Manage toddler mode →" : "Set up toddler mode →"}
      </button>
    </div>
  );
}

function DeviceCalendarOnlyRow() {
  const [mode, setMode] = useState(getDeviceDashboardMode() || "default");

  const apply = (next) => {
    setMode(next);
    setDeviceDashboardMode(next === "default" ? null : next);
    toast.success(
      next === "default"
        ? "This device follows the family setting again"
        : next === "full"
          ? "This device will show the full dashboard"
          : next === "calendar"
            ? "This device will show the calendar only"
            : "This device will show the pet log only"
    );
  };

  return (
    <div className="pt-4 border-t border-[color:var(--line-soft)]">
      <label className="label-tiny block mb-2">Dashboard on this device</label>
      <div
        className="space-y-2"
        role="radiogroup"
        aria-label="Dashboard on this device"
        data-testid="device-dashboard-mode"
      >
        {[
          { key: "default", label: "Follow family setting", sub: "Whatever the family picked — the default." },
          { key: "full", label: "Check-in mode", sub: "Check-in cards, mood headline, small calendar." },
          { key: "calendar", label: "Calendar mode", sub: "Events fill the dashboard, no check-in cards." },
          { key: "pets", label: "Pets only", sub: "Show only the pet log here. Everything else hides." },
        ].map((opt) => (
          <label
            key={opt.key}
            className="flex items-start gap-3 cursor-pointer"
            data-testid={`device-dashboard-mode-row-${opt.key}`}
          >
            <input
              type="radio"
              name="device-dashboard-mode"
              value={opt.key}
              checked={mode === opt.key}
              onChange={() => apply(opt.key)}
              className="mt-1 w-4 h-4 accent-[color:var(--ink)]"
              data-testid={`device-dashboard-mode-${opt.key}`}
            />
            <div className="flex-1">
              <div className="text-sm text-[color:var(--ink)]">{opt.label}</div>
              <div className="text-[12px] text-[color:var(--ink-3)] leading-tight">
                {opt.sub}
              </div>
            </div>
          </label>
        ))}
      </div>
      <div className="mt-3 max-w-lg text-[13px] leading-snug text-[color:var(--ink-2)]">
        Useful if you're part of the family but would rather skip the mood
        half, or a family member who only cares about the pets, or a
        dedicated kitchen iPad you want to pin to the calendar.
      </div>
    </div>
  );
}

function SettingsPinLockRow() {
  const [required, setRequired] = useState(getSettingsPinRequired());
  const toggle = () => {
    const next = !required;
    setSettingsPinRequired(next);
    setRequired(next);
    toast.success(
      next
        ? "Settings now asks for the family PIN on this device"
        : "Settings unlocked on this device — no PIN required"
    );
  };
  return (
    <div className="pt-4 border-t border-[color:var(--line-soft)]">
      <label className="label-tiny block mb-2">Settings PIN on this device</label>
      <div className="max-w-lg text-[13px] leading-snug text-[color:var(--ink-2)] mb-3">
        By default, opening Settings asks for the family PIN — safe for shared
        iPads and kitchen displays. Turn it off for a personal phone you
        don't want to type a PIN into every time.
      </div>
      <button
        type="button"
        onClick={toggle}
        className={`ff-btn inline-flex items-center gap-2 ${required ? "ff-btn-primary" : ""}`}
        data-testid="settings-pin-lock-toggle"
        aria-pressed={required}
      >
        {required ? (
          <>
            <Check className="w-4 h-4" strokeWidth={2} />
            PIN required
          </>
        ) : (
          "No PIN required"
        )}
      </button>
    </div>
  );
}

// -------- PIN rotation ------------------------------------------------------
// Changes the family PIN. Old private notes + ALL messages are encrypted
// with a key derived from the current PIN, so the backend permanently
// deletes them as part of the rotation. We warn the user loudly before
// they confirm — once done, there's no recovery path.
function PinRotationRow() {
  const [open, setOpen] = useState(false);
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setCurrentPin(""); setNewPin(""); setConfirmPin(""); setConfirmText("");
  };

  const submit = async () => {
    if (!currentPin.trim()) {
      toast.error("Enter your current PIN");
      return;
    }
    if (!newPin.trim() || newPin.trim().length < 6 || !/^\d+$/.test(newPin.trim())) {
      toast.error("New PIN must be at least 6 digits");
      return;
    }
    if (isWeakPin(newPin.trim())) {
      toast.error(WEAK_PIN_MESSAGE);
      return;
    }
    if (newPin.trim() === currentPin.trim()) {
      toast.error("New PIN must be different from the current one");
      return;
    }
    if (newPin.trim() !== confirmPin.trim()) {
      toast.error("PINs don't match");
      return;
    }
    if (confirmText.trim().toUpperCase() !== "DELETE") {
      toast.error('Type DELETE to confirm');
      return;
    }
    setBusy(true);
    try {
      const res = await api.rotateFamilyPin(currentPin.trim(), newPin.trim());
      // Re-derive the private-note key with the new PIN so anything written
      // going forward is readable. Old ciphertext is already server-side
      // deleted, so no decrypt-churn to worry about.
      const fid = auth.getFamilyId();
      if (fid) {
        clearPrivateNoteKey(fid);
        await setupPrivateNoteKey(newPin.trim(), fid);
      }
      const deletedCount = res?.messages_deleted ?? 0;
      toast.success(
        deletedCount > 0
          ? `PIN changed — ${deletedCount} message${deletedCount === 1 ? "" : "s"} and any private notes were deleted.`
          : "PIN changed"
      );
      setOpen(false);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change PIN");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div>
        <button
          type="button"
          className="ff-btn"
          onClick={() => setOpen(true)}
          data-testid="open-rotate-pin-btn"
        >
          Change PIN
        </button>
        <div className="label-tiny mt-2 text-[color:var(--ink-3)] max-w-lg leading-snug">
          New devices need the PIN to join. Changing the PIN deletes all
          private notes and messages — their encryption relies on the old PIN
          and can't be recovered afterwards.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 border border-[color:var(--line-soft)] rounded-xl p-4 bg-[color:var(--paper-2)]">
      <div className="flex items-start gap-2 text-[color:var(--danger, #a14231)]">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
        <div className="text-[13px] leading-snug">
          <strong>Heads up.</strong> Changing the PIN permanently deletes
          every private note and every message in this family. Encryption
          is tied to the current PIN — old content can't be unlocked with
          the new one. This cannot be undone.
        </div>
      </div>

      <div className="grid gap-2">
        <label className="label-tiny">Current PIN</label>
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={12}
          value={currentPin}
          onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ""))}
          className="ff-input max-w-[240px] font-mono tracking-widest"
          placeholder="Your current PIN"
          data-testid="rotate-pin-current"
        />
      </div>

      <div className="grid gap-2">
        <label className="label-tiny">New PIN (6+ digits)</label>
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={12}
          value={newPin}
          onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
          className="ff-input max-w-[240px] font-mono tracking-widest"
          placeholder="New PIN"
          data-testid="rotate-pin-new"
        />
      </div>

      <div className="grid gap-2">
        <label className="label-tiny">Confirm new PIN</label>
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={12}
          value={confirmPin}
          onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
          className="ff-input max-w-[240px] font-mono tracking-widest"
          placeholder="New PIN again"
          data-testid="rotate-pin-confirm"
        />
      </div>

      <div className="grid gap-2">
        <label className="label-tiny">
          Type <strong>DELETE</strong> to confirm you accept losing all
          private notes and messages
        </label>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          className="ff-input max-w-[240px] tracking-widest"
          placeholder="DELETE"
          data-testid="rotate-pin-confirm-text"
          autoCapitalize="characters"
        />
      </div>

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          className="ff-btn ff-btn-primary"
          disabled={busy}
          onClick={submit}
          data-testid="rotate-pin-submit"
        >
          {busy ? "Changing…" : "Change PIN & delete history"}
        </button>
        <button
          type="button"
          className="ff-btn"
          disabled={busy}
          onClick={() => { setOpen(false); reset(); }}
          data-testid="rotate-pin-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}




function KitchenDisplayRow() {
  const [on, setOn] = useState(isKioskDevice());
  const toggle = () => {
    const next = !on;
    setKioskDevice(next);
    setOn(next);
    toast.success(
      next
        ? "This device will open on the ambient display"
        : "This device will open on the dashboard"
    );
  };
  return (
    <div className="pt-4 border-t border-[color:var(--line-soft)]">
      <label className="label-tiny block mb-2">Kitchen display</label>
      <button
        type="button"
        onClick={toggle}
        className={`ff-btn inline-flex items-center gap-2 ${
          on ? "ff-btn-primary" : ""
        }`}
        data-testid="settings-kiosk-toggle"
      >
        <Tv className="w-4 h-4" strokeWidth={1.75} />
        {on ? "This is the kitchen display" : "Use this device as the kitchen display"}
      </button>
      <div className="mt-3 max-w-lg text-[13px] leading-snug text-[color:var(--ink-2)]">
        {on
          ? "This iPad will always open on the ambient display. Tap the small × in the corner to return to the dashboard."
          : "This iPad will always open on the ambient display — no need to tap the display button in the menu each time."}
      </div>
      {!on && (
        <details
          className="mt-3 max-w-lg group"
          data-testid="settings-kiosk-howto"
        >
          <summary className="cursor-pointer list-none inline-flex items-center gap-1 text-[13px] font-medium text-[color:var(--ink-2)] hover:text-[color:var(--ink)]">
            <span className="group-open:hidden">How to set it up →</span>
            <span className="hidden group-open:inline">Hide steps ↑</span>
          </summary>
          <ol className="mt-2 space-y-1.5 text-[13px] leading-snug text-[color:var(--ink-2)] list-decimal pl-5">
            <li>Add FamFeel to this device's home screen (the row above).</li>
            <li>Open FamFeel from the <strong className="text-[color:var(--ink)]">Ff</strong> icon so it launches full-screen.</li>
            <li>Come back here and tap the button above.</li>
            <li>Close and re-open — the iPad will now land straight on the ambient display.</li>
          </ol>
        </details>
      )}
    </div>
  );
}

function AddToHomeScreenRow() {
  const [installed, setInstalled] = useState(isStandalone());
  const [deferredPrompt, setDeferredPrompt] = useState(
    typeof window !== "undefined" ? window.__ffDeferredPrompt : null
  );
  const [showSteps, setShowSteps] = useState(false);

  useEffect(() => {
    if (installed) return;
    const onReady = () => setDeferredPrompt(window.__ffDeferredPrompt || null);
    const onBeforeInstall = (e) => {
      e.preventDefault();
      window.__ffDeferredPrompt = e;
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      markInstalled();
      setInstalled(true);
      setDeferredPrompt(null);
      window.__ffDeferredPrompt = null;
    };
    window.addEventListener("famfeel:install-ready", onReady);
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    if (!deferredPrompt && window.__ffDeferredPrompt) {
      setDeferredPrompt(window.__ffDeferredPrompt);
    }
    return () => {
      window.removeEventListener("famfeel:install-ready", onReady);
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installed]);

  const handleClick = async () => {
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice && choice.outcome === "accepted") {
          markInstalled();
          setInstalled(true);
        }
      } catch {/* noop */}
      setDeferredPrompt(null);
      window.__ffDeferredPrompt = null;
      return;
    }
    setShowSteps((s) => !s);
  };

  const ios = isIos();

  return (
    <div className="pt-4 border-t border-[color:var(--line-soft)]">
      <label className="label-tiny block mb-2">Add to home screen</label>
      {installed ? (
        <div
          className="flex items-center gap-2 text-[14px] text-[color:var(--ink-2)]"
          data-testid="settings-install-installed"
        >
          <Check className="w-4 h-4" strokeWidth={2} />
          <span>This device has FamFeel on its home screen.</span>
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={handleClick}
            className="ff-btn inline-flex items-center gap-2"
            data-testid="settings-install-btn"
          >
            <Smartphone className="w-4 h-4" strokeWidth={1.75} />
            {deferredPrompt ? "Add FamFeel to this device" : "Show instructions"}
          </button>
          {showSteps && !deferredPrompt && (
            <ol
              className="mt-3 space-y-2 text-[14px] leading-snug text-[color:var(--ink-2)] list-decimal pl-5"
              data-testid="settings-install-steps"
            >
              {ios ? (
                <>
                  <li>Tap the <strong className="text-[color:var(--ink)]">Share</strong> icon at the bottom of Safari.</li>
                  <li>Scroll down and tap <strong className="text-[color:var(--ink)]">Add to Home Screen</strong>.</li>
                  <li>The FamFeel <strong className="text-[color:var(--ink)]">Ff</strong> icon will appear — tap it to open full-screen.</li>
                </>
              ) : (
                <>
                  <li>Open the browser menu (<strong className="text-[color:var(--ink)]">⋮</strong> or <strong className="text-[color:var(--ink)]">⋯</strong>).</li>
                  <li>Tap <strong className="text-[color:var(--ink)]">Install app</strong> or <strong className="text-[color:var(--ink)]">Add to Home screen</strong>.</li>
                  <li>Confirm — the FamFeel <strong className="text-[color:var(--ink)]">Ff</strong> icon will appear on your home screen.</li>
                </>
              )}
            </ol>
          )}
          <div className="label-tiny mt-2 text-[color:var(--ink-3)]">
            Each device does this once. Then everyone taps the Ff icon to land straight on the family dashboard.
          </div>
        </>
      )}
    </div>
  );
}

function CustomPlanEditor({ title, items, onChange, testId }) {
  const [value, setValue] = useState("");
  const add = () => {
    const v = value.trim();
    if (!v) return;
    if (items.includes(v)) {
      toast.error("Already added");
      return;
    }
    onChange([...items, v]);
    setValue("");
  };
  const remove = (p) => onChange(items.filter((x) => x !== p));
  return (
    <div>
      <label className="label-tiny block mb-2">{title}</label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {items.length === 0 && (
          <span className="label-tiny text-[color:var(--ink-3)]">
            None yet — add one below
          </span>
        )}
        {items.map((p) => (
          <span
            key={p}
            className="ff-chip inline-flex items-center gap-1"
            data-testid={`${testId}-chip-${p.replace(/\s+/g, "-").toLowerCase()}`}
          >
            {p}
            <button
              type="button"
              onClick={() => remove(p)}
              className="opacity-60 hover:opacity-100 transition-opacity"
              aria-label={`Remove ${p}`}
            >
              <X className="w-3 h-3" strokeWidth={2.5} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          maxLength={40}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="e.g. Dance class, Late night shift"
          className="ff-input flex-1"
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={add}
          className="ff-btn"
          data-testid={`${testId}-add`}
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          Add
        </button>
      </div>
    </div>
  );
}


function relativeTimeShort(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diffSec = (Date.now() - d.getTime()) / 1000;
    if (diffSec < 60) return "just now";
    const mins = Math.round(diffSec / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString("en-US");
  } catch {
    return "—";
  }
}

function ActiveSessions() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await api.listSessions();
      setRows(r);
    } catch {
      setRows([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const kick = async (s) => {
    if (!window.confirm(`Sign out "${s.ua}"? That device will drop back to the join screen on next use.`)) return;
    setBusy(s.id);
    try {
      await api.kickSession(s.id);
      setRows((prev) => (prev || []).filter((x) => x.id !== s.id));
      toast.success(`Signed out ${s.ua}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't sign out that device");
    } finally {
      setBusy(null);
    }
  };

  if (rows === null) {
    return <div className="text-[14px] text-[color:var(--ink-3)]">Loading…</div>;
  }
  if (rows.length === 0) {
    return <div className="text-[14px] text-[color:var(--ink-3)]">No signed-in devices found.</div>;
  }

  return (
    <ul className="space-y-2" data-testid="sessions-list">
      {rows.map((s) => (
        <li
          key={s.id}
          className="ff-card p-3 flex items-center gap-3"
          data-testid={`session-row-${s.id}`}
        >
          <div className="flex-1 min-w-0">
            <div className="text-[15px] leading-tight text-[color:var(--ink)] truncate">
              {s.ua}
              {s.is_current && (
                <span
                  className="ml-2 label-tiny inline-flex items-center px-1.5 py-0.5 rounded-full bg-[#CDE6D2] border border-[#7db389] align-middle"
                  data-testid="session-current-badge"
                >
                  This device
                </span>
              )}
            </div>
            <div className="text-[12px] text-[color:var(--ink-3)] mt-0.5">
              Last active {relativeTimeShort(s.last_seen)}
              {s.ip_fragment ? ` · ${s.ip_fragment}` : ""}
              {" · "}signed in {relativeTimeShort(s.created_at)}
            </div>
          </div>
          {!s.is_current && (
            <button
              type="button"
              onClick={() => kick(s)}
              disabled={busy === s.id}
              className="ff-btn text-[color:var(--rough-strong)]"
              data-testid={`session-kick-${s.id}`}
              aria-label={`Sign out ${s.ua}`}
              title="Sign out this device"
            >
              <LogOut className="w-4 h-4" strokeWidth={2} />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function SignOutOthersRow() {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const onConfirm = async () => {
    if (!pin.trim()) {
      toast.error("Enter your family PIN");
      return;
    }
    setBusy(true);
    try {
      const res = await api.signOutOthers({ pin: pin.trim() });
      if (res?.token) {
        // Swap the locally-cached JWT so this device keeps working with the
        // new epoch; everyone else's old token is now 401.
        localStorage.setItem("famfeel_token", res.token);
      }
      setOpen(false);
      setPin("");
      toast.success("Other devices have been signed out.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't sign out other devices";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="ff-btn text-[color:var(--rough-strong)] inline-flex items-center gap-1"
          data-testid="sign-out-others-btn"
          title="Invalidates every other signed-in device for this family"
        >
          Sign out all other devices
        </button>
        <span className="label-tiny text-[color:var(--ink-3)] max-w-md leading-snug">
          Use this if a phone or laptop you no longer trust is still signed in.
          Every other device will drop back to the join screen.
        </span>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[color:var(--line-soft)] p-4 bg-[color:var(--bg-soft)]">
      <p className="text-[14px] leading-snug mb-3 max-w-md">
        Confirm your family PIN to sign out every other device. This one stays
        signed in.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="password"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          placeholder="Family PIN"
          autoFocus
          maxLength={32}
          className="ff-input max-w-[180px]"
          data-testid="sign-out-others-pin"
        />
        <button
          onClick={onConfirm}
          disabled={busy}
          className="ff-btn ff-btn-primary"
          data-testid="sign-out-others-confirm"
        >
          {busy ? "Signing out…" : "Sign out others"}
        </button>
        <button
          onClick={() => {
            setOpen(false);
            setPin("");
          }}
          className="ff-btn"
          data-testid="sign-out-others-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}



function DeviceIdentityRow({ viewerId, setViewerId, members }) {
  // "Who's using this device?" — the key identity claim for push
  // routing (so a walk reminder for Teo reaches Teo's phone, not the
  // kitchen iPad), check-in pre-fill, and the "Mum has Drinks with
  // Mia" broadcast attribution. With only one shared family login
  // there's no auth-layer answer to "who's on this device?" — this
  // setting is the closest thing we've got.
  //
  // Source of truth is `famfeel_viewer_id` in localStorage (via
  // App.js -> setViewerId). After persisting, we call resyncPush so
  // the backend's push_subscriptions row for this device picks up
  // the new member_id immediately — otherwise the old claim lingers
  // until the next permission re-grant.
  const [busy, setBusy] = useState(false);
  // Pets can't "be on a device" for push-routing purposes; don't
  // show them in the picker.
  const humans = (members || []).filter((m) => m.role !== "pet");
  const current = humans.find((m) => m.id === viewerId);
  const choose = async (nextId) => {
    if (busy) return;
    setBusy(true);
    try {
      if (typeof setViewerId === "function") setViewerId(nextId || null);
      // Tell the backend the new member_id for this device's push
      // sub. Best-effort — if notifications aren't enabled on this
      // device yet there's nothing to sync; the claim still lives
      // in localStorage and will be picked up on next subscribe.
      try { await resyncPush(nextId || null); } catch { /* noop */ }
      if (nextId) {
        const picked = humans.find((m) => m.id === nextId);
        toast.success(picked ? `Set to ${picked.name}` : "Device updated");
      } else {
        toast.success("Device reset to shared");
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="pt-4 border-t border-[color:var(--line-soft)]" data-testid="device-identity-row">
      <label className="label-tiny block mb-2">Who's on this device?</label>
      <div className="max-w-lg text-[13px] leading-snug text-[color:var(--ink-2)] mb-3">
        Helps route push notifications (so your walk reminder reaches
        your phone, not the kitchen iPad) and pre-fills check-ins. Pick
        "Shared device" for the kitchen iPad or any shared tablet.
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => choose(null)}
          disabled={busy}
          className={`ff-btn ff-btn-sm ${!current ? "ff-btn-primary" : ""}`}
          data-testid="device-identity-shared"
        >
          Shared device
        </button>
        {humans.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => choose(m.id)}
            disabled={busy}
            className={`ff-btn ff-btn-sm inline-flex items-center gap-2 ${current?.id === m.id ? "ff-btn-primary" : ""}`}
            data-testid={`device-identity-member-${m.id}`}
          >
            <span className="w-6 h-6 rounded-full bg-[color:var(--bg)] border border-[color:var(--surface)] inline-flex items-center justify-center overflow-hidden text-sm">
              {m.avatar_image ? (
                <img src={m.avatar_image} alt="" className="w-full h-full object-cover" />
              ) : (
                m.avatar_emoji
              )}
            </span>
            <span>I am {m.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// Friendly human line for Plan & billing. Branches on real-Stripe vs. one-time
// fallback, and on whether auto-renew is on, cancel-at-period-end, or lapsed.
function billingSummaryText(s) {
  if (!s) return "Review your plan, upgrade, or extend your subscription.";
  const tierLabel = {
    free: "Free",
    plus: "Plus",
    grand: "Grand",
    extended: "Extended Family",
  }[s.tier] || "Free";
  if (s.tier === "free") {
    return "Free · up to 4 family members. Upgrade to add more.";
  }
  if (s.admin_override) {
    return `${tierLabel} · complimentary access, continues automatically.`;
  }
  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
      });
    } catch {
      return "soon";
    }
  };
  if (s.auto_renew && s.expires_at) {
    return `${tierLabel} · auto-renews ${fmt(s.expires_at)}.`;
  }
  if (s.cancel_at_period_end && s.expires_at) {
    return `${tierLabel} · cancels ${fmt(s.expires_at)}. Resume anytime before then.`;
  }
  if (s.is_active && s.expires_at) {
    return `${tierLabel} · active until ${fmt(s.expires_at)}.`;
  }
  return `${tierLabel} plan.`;
}

export default function FamilySettingsPanel({ settings, onSaved, onSignOut, viewerId, setViewerId, tier, onOpenBilling, billingSummary, onHousesChanged, members = [] }) {
  const { t } = useI18n();
  const [familyName, setFamilyName] = useState(settings.family_name);
  const [tz, setTz] = useState(settings.timezone);
  const [customAdult, setCustomAdult] = useState(
    settings.custom_plans_adult || []
  );
  const [customKid, setCustomKid] = useState(settings.custom_plans_kid || []);
  const [dashboardMode, setDashboardMode] = useState(
    settings.dashboard_mode || (settings.calendar_only ? "calendar" : "full")
  );
  const [calendarOnlyHidePets, setCalendarOnlyHidePets] = useState(!!settings.calendar_only_hide_pets);
  // Time format — "auto" (browser default), "24h" (force 14:30), "12h"
  // (force 2:30 PM). The toggle below maps "auto" + "12h" both to "off"
  // so the UI stays a single switch instead of a tri-state radio group.
  const [timeFormat, setTimeFormat] = useState(settings.time_format || "auto");
  // Chore push routing — "assignee_and_shared" (default) limits the
  // chore reminder to the assignee + kitchen/shared devices;
  // "everyone" fans out to the whole family.
  const [chorePushTarget, setChorePushTarget] = useState(
    settings.chore_push_target || "assignee_and_shared",
  );
  const [saving, setSaving] = useState(false);
  // Bring-your-own Google AI key — powers the flyer scanner. We never
  // pull the key back from the server; the panel only shows a
  // "Connected" pill when it's set, plus a "Replace" / "Disconnect"
  // pair. Saving an empty string is treated as "no change" client-side
  // so families can re-save the form without losing their key.
  const [byoKeyInput, setByoKeyInput] = useState("");
  const [byoKeyBusy, setByoKeyBusy] = useState(false);
  const [byoKeyConnected, setByoKeyConnected] = useState(
    !!settings.byo_google_ai_key_set,
  );
  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [note, setNote] = useState("");
  const [noteEmail, setNoteEmail] = useState("");
  const [noteSending, setNoteSending] = useState(false);
  const [noteSent, setNoteSent] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [toddlerOpen, setToddlerOpen] = useState(false);
  // Two-tab settings: "family" (affects everyone) vs "device" (only this phone/iPad).
  // Splits the long scroll into two bite-sized pages so people don't bounce.
  const [tab, setTab] = useState("family");

  useEffect(() => {
    setFamilyName(settings.family_name);
    setTz(settings.timezone);
    setCustomAdult(settings.custom_plans_adult || []);
    setCustomKid(settings.custom_plans_kid || []);
    setDashboardMode(settings.dashboard_mode || (settings.calendar_only ? "calendar" : "full"));
    setCalendarOnlyHidePets(!!settings.calendar_only_hide_pets);
    setTimeFormat(settings.time_format || "auto");
    setChorePushTarget(settings.chore_push_target || "assignee_and_shared");
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        family_name: familyName.trim() || null,
        timezone: tz,
        custom_plans_adult: customAdult,
        custom_plans_kid: customKid,
        dashboard_mode: dashboardMode,
        calendar_only_hide_pets: calendarOnlyHidePets,
        time_format: timeFormat,
        chore_push_target: chorePushTarget,
      };
      const updated = await api.updateFamilySettings(payload);
      // Apply the chosen time format to the global immediately so the
      // dashboard re-renders with the new format the moment Save is
      // tapped — no page refresh required.
      applyTimeFormatGlobal(updated?.time_format || timeFormat);
      toast.success("Saved");
      onSaved(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const rotateCode = async () => {
    if (
      !window.confirm(
        "Generate a new join code? The old code will stop working for new devices. Already-signed-in devices stay connected."
      )
    ) {
      return;
    }
    setRotating(true);
    try {
      const updated = await api.rotateJoinCode();
      onSaved(updated);
      toast.success("New join code generated");
    } catch (e) {
      toast.error("Could not rotate code");
    } finally {
      setRotating(false);
    }
  };

  const copyCode = async () => {
    if (!settings?.join_code) return;
    try {
      await navigator.clipboard.writeText(settings.join_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.info("Copy manually");
    }
  };

  // Build a pre-filled invite URL that drops the recipient straight into the
  // onboarding form with the join code already typed in. They still need the
  // family PIN, which we deliberately do NOT put in the link (PIN stays in
  // the family, shared verbally or over a trusted channel).
  const inviteUrl = settings?.join_code
    ? `${window.location.origin}/?join=${encodeURIComponent(settings.join_code)}`
    : null;

  const [inviteCopied, setInviteCopied] = React.useState(false);
  const shareInvite = async () => {
    if (!inviteUrl) return;
    const familyLabel = settings?.family_name
      ? `the ${settings.family_name} family`
      : "our family";
    const text =
      `Join ${familyLabel} on FamFeel — a calm shared dashboard for how everyone is.\n` +
      `Tap the link, then ask me for our 6-digit family PIN.`;
    // Native share sheet on phones (iOS/Android), with graceful clipboard
    // fallback everywhere else — and a second try at raw-text copy if the
    // URL copy fails for any reason.
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: "Join our FamFeel family",
          text,
          url: inviteUrl,
        });
        return;
      } catch {
        // User dismissed share sheet — silently fall back to copy.
      }
    }
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 1800);
      toast.success("Invite link copied");
    } catch {
      toast.info("Copy this link: " + inviteUrl);
    }
  };

  const deleteFamily = async () => {
    setDeleting(true);
    try {
      await api.deleteFamily();
      toast.success("Family deleted");
      // Full teardown: clear token and return to landing
      if (onSignOut) onSignOut();
      else {
        // Fallback: hard reload to root
        window.location.href = "/";
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete family");
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="family-settings-panel">
      {/* Tabs — split the long scroll into a Family page and a Device page.
          People bounced when everything lived on one endless form. */}
      <div
        role="tablist"
        className="flex gap-1 p-1 rounded-full bg-[color:var(--bg-soft)] w-fit"
        data-testid="settings-tabs"
      >
        {[
          { key: "family", label: "Family" },
          { key: "device", label: "This device" },
        ].map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              tab === t.key
                ? "bg-[color:var(--surface)] text-[color:var(--ink)] shadow-sm"
                : "text-[color:var(--ink-2)] hover:text-[color:var(--ink)]"
            }`}
            data-testid={`settings-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "family" && (
      <>
      <QuickGuidesRow tier={tier} />
      <div className="space-y-3">
        <div>
          <div className="label-tiny">Dashboard mode</div>
          <div className="text-sm text-[color:var(--ink-2)] mt-1 max-w-lg leading-snug">
            Choose what fills the dashboard for everyone in the family. Each
            member can still override this on their own device.
          </div>
        </div>
        <div className="space-y-2" role="radiogroup" aria-label="Dashboard mode">
          {[
            { key: "full", label: "Check-in mode", sub: "Check-in cards, mood headline, small calendar" },
            { key: "calendar", label: "Calendar mode", sub: "Events fill the dashboard, no check-in cards." },
            { key: "pets", label: "Pets only", sub: "Pet log fills the dashboard, human cards hide." },
          ].map((opt) => (
            <label
              key={opt.key}
              className="flex items-start gap-3 cursor-pointer"
              data-testid={`dashboard-mode-row-${opt.key}`}
            >
              <input
                type="radio"
                name="dashboard-mode"
                value={opt.key}
                checked={dashboardMode === opt.key}
                onChange={() => setDashboardMode(opt.key)}
                className="mt-1 w-4 h-4 accent-[color:var(--ink)]"
                data-testid={`dashboard-mode-${opt.key}`}
              />
              <div className="flex-1">
                <div className="text-sm text-[color:var(--ink)]">{opt.label}</div>
                <div className="text-[12px] text-[color:var(--ink-3)] leading-tight">
                  {opt.sub}
                </div>
              </div>
            </label>
          ))}
        </div>
        {dashboardMode === "calendar" && (
          <div className="text-[12px] text-[color:var(--ink-3)] leading-snug ml-7 max-w-lg">
            Morning check-in notifications will stop. Birthday reminders and
            dog-walk reminders still fire.
          </div>
        )}
        {dashboardMode === "pets" && (
          <div className="text-[12px] text-[color:var(--ink-3)] leading-snug ml-7 max-w-lg">
            Morning check-in notifications will stop. Dog-walk reminders
            still fire.
          </div>
        )}
        {dashboardMode === "calendar" && (
          <label
            className="flex items-center gap-3 cursor-pointer ml-6"
            data-testid="calendar-only-hide-pets-row"
          >
            <input
              type="checkbox"
              checked={calendarOnlyHidePets}
              onChange={(e) => setCalendarOnlyHidePets(e.target.checked)}
              className="w-4 h-4 accent-[color:var(--ink)]"
              data-testid="calendar-only-hide-pets"
            />
            <span className="text-sm text-[color:var(--ink-2)]">
              Also hide pet cards (dog walks, feeds, water)
            </span>
          </label>
        )}
      </div>
      <div className="pt-4 border-t border-[color:var(--line-soft)]">
        <label className="label-tiny block mb-2">
          {t({ en: "Family name", da: "Familienavn" })}
        </label>
        <input
          value={familyName}
          onChange={(e) => setFamilyName(e.target.value)}
          className="ff-input"
          data-testid="family-name-input"
        />
      </div>
      <div>
        <label className="label-tiny block mb-2">
          {t({ en: "Timezone", da: "Tidszone" })}
        </label>
        <select
          value={tz}
          onChange={(e) => setTz(e.target.value)}
          className="ff-input"
          data-testid="family-tz-select"
        >
          {TIMEZONES.map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </select>
        <div className="label-tiny mt-1">
          {t({
            en: "Used for midnight reset of check-ins",
            da: "Bruges til midnats-nulstilling af tjek-ins",
          })}
        </div>
      </div>
      <div>
        <label className="label-tiny block mb-2">
          {t({ en: "Time format", da: "Tidsformat" })}
        </label>
        <div
          className="inline-flex rounded-full border border-[color:var(--line-soft)] bg-[color:var(--bg-soft)] p-0.5"
          data-testid="settings-time-format"
        >
          {[
            { v: "auto", label: t({ en: "Auto", da: "Auto" }) },
            { v: "24h", label: "14:30" },
            { v: "12h", label: "2:30 PM" },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setTimeFormat(opt.v)}
              className={`px-3 py-1.5 text-[13px] rounded-full transition-colors ${
                timeFormat === opt.v
                  ? "bg-[color:var(--ink)] text-[color:var(--bg)]"
                  : "text-[color:var(--ink-2)] hover:text-[color:var(--ink)]"
              }`}
              data-testid={`time-format-${opt.v}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="label-tiny block mb-2">Chore reminders</label>
        <div
          className="inline-flex rounded-full border border-[color:var(--line-soft)] bg-[color:var(--bg-soft)] p-0.5"
          data-testid="settings-chore-push-target"
        >
          {[
            { v: "assignee_and_shared", label: "Assignee + shared" },
            { v: "everyone", label: "Everyone" },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setChorePushTarget(opt.v)}
              className={`px-3 py-1.5 text-[13px] rounded-full transition-colors ${
                chorePushTarget === opt.v
                  ? "bg-[color:var(--ink)] text-[color:var(--bg)]"
                  : "text-[color:var(--ink-2)] hover:text-[color:var(--ink)]"
              }`}
              data-testid={`chore-push-target-${opt.v}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="mt-1.5 text-[11px] text-[color:var(--ink-3)] max-w-md leading-relaxed">
          Where chore reminders go when a chore has a time set.{" "}
          {chorePushTarget === "assignee_and_shared"
            ? "Only the assignee's signed-in devices and the kitchen iPad (any device that hasn't picked a profile)."
            : "Every signed-in device in the family — quieter dashboards, louder phones."}
        </p>
      </div>
      <div className="pt-4 border-t border-[color:var(--line-soft)]">
        <div className="label-tiny mb-2">Join code (for new devices)</div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="font-display text-3xl tabular tracking-widest select-all flex-1 min-w-[140px]"
            data-testid="settings-join-code"
          >
            {settings.join_code || "—"}
          </div>
          <button
            type="button"
            onClick={copyCode}
            className="ff-btn"
            data-testid="settings-copy-code"
          >
            <Copy className="w-4 h-4" strokeWidth={2} />
            {copied ? "Copied" : "Copy"}
          </button>
          <button
            type="button"
            onClick={shareInvite}
            className="ff-btn"
            data-testid="settings-share-invite"
            title="Share a one-tap invite link (PIN shared separately)"
          >
            <Share2 className="w-4 h-4" strokeWidth={2} />
            {inviteCopied ? "Link copied" : "Share invite"}
          </button>
          <button
            type="button"
            onClick={rotateCode}
            disabled={rotating}
            className="ff-btn"
            data-testid="settings-rotate-code"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={2} />
            {rotating ? "Rotating…" : "New code"}
          </button>
          <button
            type="button"
            onClick={() => setShowQr((v) => !v)}
            className="ff-btn"
            data-testid="settings-toggle-qr"
          >
            <QrCode className="w-4 h-4" strokeWidth={2} />
            {showQr ? "Hide QR" : "Show QR"}
          </button>
          <a
            href="/family/poster"
            target="_blank"
            rel="noopener noreferrer"
            className="ff-btn"
            data-testid="settings-kitchen-poster"
            title="Open a print-ready A4 poster — stick it on the kitchen iPad"
          >
            <Printer className="w-4 h-4" strokeWidth={2} />
            Kitchen poster
          </a>
        </div>
        {showQr && settings.join_code && (
          <div
            className="mt-3 flex justify-center bg-white rounded-xl p-4 border border-[color:var(--line-soft)]"
            data-testid="settings-qr"
          >
            <QRCodeSVG
              value={`${window.location.origin}/?join=${encodeURIComponent(
                settings.join_code
              )}`}
              size={180}
              level="M"
            />
          </div>
        )}
        <div className="label-tiny mt-2 text-[color:var(--ink-3)]">
          Share the code + family PIN with each new device. Scan the QR from
          another phone to skip typing the code. Rotate if someone loses a
          device.
        </div>
      </div>

      <div className="pt-4 border-t border-[color:var(--line-soft)]">
        <label className="label-tiny block mb-2">
          Family PIN
        </label>
        <PinRotationRow />
      </div>

      <HousesPanelMovedNotice tier={tier} />

      {/* Flyer scanner — Bring-your-own Google AI key.
          Cost-free for FamFeel: each family supplies their own free
          Google AI Studio key (Google grants every Gmail account a
          generous daily quota on Gemini Flash). The key is stored on
          the family doc, used only by `/api/events/from-image`, and
          never echoed back to the client. Sits directly above the
          ICS calendar subscriptions because both blocks are about
          getting external events into the family calendar. */}
      <div
        className="pt-4 border-t border-[color:var(--line-soft)]"
        data-testid="settings-byo-google-key"
      >
        <div className="flex items-baseline justify-between mb-2 gap-2">
          <div className="label-tiny">📸 Flyer scanner (Google AI key)</div>
          {byoKeyConnected && (
            <span
              className="text-[10px] uppercase tracking-[0.06em] font-semibold px-2 py-0.5 rounded-full bg-[#CDE6D2] text-[#2A4A33]"
              data-testid="settings-byo-key-connected"
            >
              ● Connected
            </span>
          )}
        </div>
        <p className="text-[13px] text-[color:var(--ink-2)] leading-snug mb-3">
          Lets the calendar lift events out of any school flyer or
          sports schedule photo. Uses Google's free quota — connect once
          and you're done.{" "}
          <a
            href="https://aistudio.google.com/apikey"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2"
          >
            Get a free key →
          </a>
        </p>
        {!byoKeyConnected || byoKeyInput ? (
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="password"
              value={byoKeyInput}
              onChange={(e) => setByoKeyInput(e.target.value)}
              placeholder="AIza…"
              className="ff-input flex-1 min-w-[200px] tabular"
              data-testid="settings-byo-key-input"
              autoComplete="off"
            />
            <button
              type="button"
              disabled={byoKeyBusy || !byoKeyInput.trim()}
              onClick={async () => {
                setByoKeyBusy(true);
                try {
                  await api.setByoGoogleKey(byoKeyInput.trim());
                  setByoKeyConnected(true);
                  setByoKeyInput("");
                  toast.success("Flyer scanner connected");
                  if (onSaved) onSaved();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Couldn't save that key");
                } finally {
                  setByoKeyBusy(false);
                }
              }}
              className="ff-btn ff-btn-primary disabled:opacity-50"
              data-testid="settings-byo-key-save"
            >
              {byoKeyBusy ? "Saving…" : byoKeyConnected ? "Replace" : "Connect"}
            </button>
            {byoKeyConnected && (
              <button
                type="button"
                onClick={() => setByoKeyInput("")}
                className="ff-btn"
              >
                Cancel
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setByoKeyInput(" ")}
              className="ff-btn"
              data-testid="settings-byo-key-replace"
            >
              Replace key
            </button>
            <button
              type="button"
              disabled={byoKeyBusy}
              onClick={async () => {
                if (!window.confirm("Disconnect Google AI key? The flyer scanner will stop working.")) return;
                setByoKeyBusy(true);
                try {
                  await api.clearByoGoogleKey();
                  setByoKeyConnected(false);
                  toast.success("Disconnected");
                  if (onSaved) onSaved();
                } catch {
                  toast.error("Couldn't disconnect");
                } finally {
                  setByoKeyBusy(false);
                }
              }}
              className="ff-btn text-[color:var(--rough-strong)] disabled:opacity-50"
              data-testid="settings-byo-key-disconnect"
            >
              {byoKeyBusy ? "…" : "Disconnect"}
            </button>
          </div>
        )}
      </div>

      {/* Calendar subscriptions — pull events from Google/iCloud/Outlook
          ICS feeds. */}
      <div className="pt-4 border-t border-[color:var(--line-soft)]">
        <CalendarSubscriptions />
      </div>

      <div className="pt-4 border-t border-[color:var(--line-soft)]">
        <label className="label-tiny block mb-2">Signed-in devices</label>
        <ActiveSessions />
      </div>

      <div className="pt-4 border-t border-[color:var(--line-soft)]">
        <label className="label-tiny block mb-2">Lost or shared device?</label>
        <SignOutOthersRow />
      </div>

      <div className="flex items-center justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="ff-btn ff-btn-primary"
          data-testid="family-settings-save"
        >
          {saving
            ? t({ en: "Saving…", da: "Gemmer…" })
            : t({ en: "Save settings", da: "Gem indstillinger" })}
        </button>
      </div>

      {/* Send the makers a note — a tiny, calm feedback pipe. The /reviews
          page invites families to submit quotes via this form. Messages
          land in the admin dashboard inbox. Rate-limited server-side. */}
      <section
        className="pt-6 mt-2 border-t border-[color:var(--line-soft)]"
        data-testid="feedback-section"
      >
        <div className="flex items-center gap-2 mb-3">
          <Mail className="w-4 h-4 text-[color:var(--ink-2)]" strokeWidth={2} />
          <span className="label-tiny">Send the makers a note</span>
        </div>
        {noteSent ? (
          <div
            className="ff-card p-4 flex items-start gap-3"
            data-testid="feedback-sent-card"
          >
            <Check className="w-5 h-5 mt-0.5 flex-shrink-0" strokeWidth={2} />
            <div>
              <div className="font-medium">Thank you — note received.</div>
              <p className="text-[13px] text-[color:var(--ink-2)] leading-snug mt-1">
                If we publish it on the reviews page we'll never use your
                real surname or your kids' names — first name + city only.
              </p>
              <button
                type="button"
                onClick={() => { setNoteSent(false); setNote(""); setNoteEmail(""); }}
                className="ff-btn mt-3 text-[13px]"
                data-testid="feedback-send-another"
              >
                Send another
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-[13px] text-[color:var(--ink-2)] leading-snug">
              Bug, feature idea, or a review you'd let us put on the{" "}
              <a
                href="/reviews"
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-2"
              >
                reviews page
              </a>
              ? Drop a note. We read every one.
            </p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Tell us something — what's working, what isn't, or leave a testimonial."
              rows={4}
              maxLength={4000}
              className="ff-input w-full resize-y min-h-[96px]"
              data-testid="feedback-message-input"
            />
            <input
              type="email"
              value={noteEmail}
              onChange={(e) => setNoteEmail(e.target.value)}
              placeholder="Your email (optional — only if you want a reply)"
              className="ff-input w-full"
              data-testid="feedback-email-input"
              autoComplete="email"
            />
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="label-tiny text-[color:var(--ink-3)]">
                {note.trim().length}/4000
              </span>
              <button
                type="button"
                disabled={noteSending || note.trim().length === 0}
                onClick={async () => {
                  setNoteSending(true);
                  try {
                    await api.sendFeedback({
                      message: note.trim(),
                      contact_email: noteEmail.trim() || undefined,
                    });
                    setNoteSent(true);
                    toast.success("Note sent — thanks!");
                  } catch (err) {
                    toast.error(
                      err?.response?.data?.detail || "Couldn't send the note",
                    );
                  } finally {
                    setNoteSending(false);
                  }
                }}
                className="ff-btn ff-btn-primary inline-flex items-center gap-1.5"
                data-testid="feedback-send-btn"
              >
                <Send className="w-3.5 h-3.5" strokeWidth={2} />
                {noteSending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Danger zone — always last so nobody scrolls past it by accident
          and taps Delete thinking it was just another button. */}
      <div className="pt-6 mt-2 border-t border-[color:var(--rough-strong)]/25">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-[color:var(--rough-strong)]" strokeWidth={2} />
          <span className="label-tiny text-[color:var(--rough-strong)]">Danger zone</span>
        </div>
        {!confirmDelete ? (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="ff-btn border-[color:var(--rough-strong)]/40 text-[color:var(--rough-strong)]"
            data-testid="delete-family-open"
          >
            Delete entire family…
          </button>
        ) : (
          <div className="ff-card p-4 space-y-3 border-[color:var(--rough-strong)]/40">
            <div className="text-[15px] leading-snug">
              <strong>This can’t be undone.</strong> All members, check-ins,
              messages, and family settings will be permanently deleted for
              everyone.
            </div>
            <label className="label-tiny block">
              Type <strong className="tabular">DELETE</strong> to confirm
            </label>
            <input
              type="text"
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value.toUpperCase())}
              className="ff-input font-mono tracking-widest max-w-[200px]"
              data-testid="delete-family-confirm-input"
              autoFocus
            />
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  setConfirmDelete(false);
                  setDeleteText("");
                }}
                className="ff-btn"
                data-testid="delete-family-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={deleteFamily}
                disabled={deleteText !== "DELETE" || deleting}
                className="ff-btn"
                style={{
                  background:
                    deleteText === "DELETE"
                      ? "var(--rough-strong)"
                      : "transparent",
                  color: deleteText === "DELETE" ? "var(--bg)" : "var(--ink-3)",
                  borderColor:
                    deleteText === "DELETE"
                      ? "var(--rough-strong)"
                      : "var(--line)",
                }}
                data-testid="delete-family-confirm"
              >
                {deleting ? "Deleting…" : "Yes, delete forever"}
              </button>
            </div>
          </div>
        )}
      </div>

      {onOpenBilling && (
        <div className="pt-4 mt-4 border-t border-[color:var(--line-soft)]">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display text-base tracking-tight">Plan &amp; billing</div>
              <div className="label-tiny text-[color:var(--ink-2)] mt-0.5" data-testid="billing-summary">
                {billingSummaryText(billingSummary)}
              </div>
            </div>
            <button
              onClick={onOpenBilling}
              className="ff-btn"
              data-testid="open-billing-btn"
            >
              Manage billing
            </button>
          </div>
        </div>
      )}
      </>
      )}

      {tab === "device" && (
      <>
        <div className="text-[13px] leading-snug text-[color:var(--ink-2)] max-w-lg">
          These settings only affect <strong>this phone or iPad</strong>. Other
          family devices stay as they were.
        </div>

        {/* Notifications moved to the top of the device tab so it's
            the first thing a new user sees — this is what the
            welcome guide tells them to do. DeviceIdentityRow
            ("Who's on this device?") follows so the user picks
            who's holding the phone right after enabling pings. */}
        <NotificationsRow viewerId={viewerId} />

        <DeviceIdentityRow
          viewerId={viewerId}
          setViewerId={setViewerId}
          members={members}
        />

        <DeviceCalendarOnlyRow />

        <AddToHomeScreenRow />

        <KitchenDisplayRow />

        <SettingsPinLockRow />

        <CalendarSubscribeRow />

        {onSignOut && (
          <div className="pt-4 border-t border-[color:var(--line-soft)]">
            <label className="label-tiny block mb-2">
              {t({ en: "Sign out this device", da: "Log ud af denne enhed" })}
            </label>
            <div className="max-w-lg text-[13px] leading-snug text-[color:var(--ink-2)] mb-3">
              {t({
                en: "Signs out just this phone/iPad. Family data stays on the server — you'll need the join code + PIN to get back in.",
                da: "Logger kun denne telefon/iPad ud. Familiens data forbliver på serveren — du skal bruge familie-koden + PIN for at komme ind igen.",
              })}
            </div>
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    t({
                      en: "Sign out on this device? Family data stays on the server — you'll need the join code + PIN to get back in.",
                      da: "Log ud af denne enhed? Familiens data forbliver på serveren — du skal bruge familie-koden + PIN for at komme ind igen.",
                    })
                  )
                ) {
                  onSignOut();
                }
              }}
              className="ff-btn text-[color:var(--rough-strong)]"
              data-testid="sign-out-btn"
            >
              <LogOut className="w-4 h-4" strokeWidth={2} />
              {t({ en: "Sign out this device", da: "Log ud af denne enhed" })}
            </button>
          </div>
        )}
      </>
      )}

      {toddlerOpen && (
        <ToddlerModeSheet onClose={() => setToddlerOpen(false)} />
      )}
    </div>
  );
}

/**
 * Small pointer where the Houses panel used to live. Houses settings
 * moved to the Family page (Members tab) so members + their houses
 * live side-by-side. Only shown for tiers that include Houses (Grand
 * and Extended) — other tiers never saw the old panel, so they see
 * nothing here.
 */
function HousesPanelMovedNotice({ tier }) {
  if (tier !== "extended" && tier !== "grand") return null;
  const goFamilyPage = () => {
    // Dispatch so MembersManager can switch its outer tab without us
    // poking its DOM directly. Keeps the decoupling clean.
    window.dispatchEvent(
      new CustomEvent("famfeel:open-manager-tab", { detail: { tab: "members" } })
    );
  };
  return (
    <div className="pt-4 border-t border-[color:var(--line-soft)]">
      <label className="label-tiny block mb-2">Houses</label>
      <div
        className="rounded-xl border border-[color:var(--line)] bg-[color:var(--bg-soft)] p-3 flex items-start gap-3"
        data-testid="houses-moved-notice"
      >
        <div className="flex-1 text-[13px] leading-snug text-[color:var(--ink-2)]">
          Houses settings moved to the <strong>Family page</strong> — where
          your members live. Add, rename, and lock houses right alongside
          who's in each one.
        </div>
        <button
          type="button"
          onClick={goFamilyPage}
          className="ff-btn text-[13px] flex-shrink-0"
          data-testid="go-family-page-btn"
        >
          Open Family
        </button>
      </div>
    </div>
  );
}

/**
 * "Show quick guide" row — surfaces both walkthroughs (Welcome + Houses).
 * Resetting the per-device dismissal flag then dispatching a window
 * event lets the Dashboard open the guide from anywhere.
 */
function QuickGuidesRow({ tier }) {
  const openGuide = (id) => {
    const dismissalKey = {
      welcome: "welcome_v1",
      discover: "discover_v1",
      pets: "pets_v1",
      houses: "houses_v1",
    }[id];
    if (dismissalKey) resetGuide(dismissalKey);
    window.dispatchEvent(
      new CustomEvent("famfeel:open-guide", { detail: { id } })
    );
  };
  return (
    <div className="pt-4 border-t border-[color:var(--line-soft)]">
      <label className="label-tiny block mb-2">Quick guides</label>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => openGuide("welcome")}
          className="ff-btn text-[13px]"
          data-testid="reopen-welcome-guide-btn"
        >
          Show Welcome guide
        </button>
        <button
          type="button"
          onClick={() => openGuide("discover")}
          className="ff-btn text-[13px]"
          data-testid="reopen-discover-guide-btn"
        >
          Show Discover guide
        </button>
        <button
          type="button"
          onClick={() => openGuide("pets")}
          className="ff-btn text-[13px]"
          data-testid="reopen-pets-guide-btn"
        >
          Show Pets guide
        </button>
        {(tier === "extended" || tier === "grand") && (
          <button
            type="button"
            onClick={() => openGuide("houses")}
            className="ff-btn text-[13px]"
            data-testid="reopen-houses-guide-btn"
          >
            Show Houses guide
          </button>
        )}
      </div>
    </div>
  );
}
